'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useState } from 'react';

/**
 * React Query Provider
 *
 * Configuración global para data fetching en toda la aplicación.
 *
 * ⚙️ Configuración UX-optimizada:
 * - Refetch automático al cambiar de pestaña (datos siempre frescos)
 * - Refetch automático al reconectar internet
 * - Cache de 30 segundos (balance perfecto entre frescura y eficiencia)
 * - NO polling por defecto (se puede habilitar por query)
 *
 * 🎯 Beneficios:
 * - Elimina llamadas múltiples a la API (10+ → 1)
 * - Cache compartido entre componentes
 * - Loading/error states automáticos
 * - Invalidación inteligente después de mutations
 *
 * @example
 * // En app/layout.tsx
 * <QueryProvider>
 *   <YourApp />
 * </QueryProvider>
 */
export function QueryProvider({ children }: { children: React.ReactNode }) {
  // useState para crear el client solo una vez (evita re-creación en hot reload)
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // ============================================
            // Cache Configuration
            // ============================================

            /**
             * staleTime: Tiempo que los datos se consideran "frescos"
             * Mientras están frescos, NO se hace refetch automático.
             *
             * 30 segundos = Balance perfecto:
             * - No tan corto que cause fetches excesivos
             * - No tan largo que los datos queden obsoletos
             */
            staleTime: 30 * 1000, // 30 segundos

            /**
             * gcTime: Tiempo que los datos se mantienen en cache
             * después de que ya no se usan (garbage collection).
             *
             * 5 minutos = Si el usuario vuelve a la pantalla en <5min,
             * los datos cached se muestran instantáneamente.
             *
             * Antes se llamaba "cacheTime" (React Query v4)
             */
            gcTime: 5 * 60 * 1000, // 5 minutos

            // ============================================
            // Refetch Behavior (UX-first)
            // ============================================

            /**
             * ✅ Refetch al cambiar de pestaña
             *
             * Escenario: Usuario tiene la app abierta, cambia a otra pestaña
             * por 5 minutos, vuelve a la app.
             *
             * Comportamiento: Refetch automático → Datos frescos
             *
             * Beneficio: Usuario siempre ve datos actualizados sin hacer nada.
             */
            refetchOnWindowFocus: true,

            /**
             * ✅ Refetch al reconectar internet
             *
             * Escenario: Usuario pierde conexión y la recupera.
             *
             * Comportamiento: Refetch automático → Sincroniza datos
             *
             * Beneficio: Recuperación automática sin intervención del usuario.
             */
            refetchOnReconnect: true,

            /**
             * ✅ Refetch al montar si los datos están stale
             *
             * Escenario: Usuario navega a /users, luego a /roles, luego
             * vuelve a /users (después de 30s).
             *
             * Comportamiento:
             * - Si <30s: Muestra cache (instantáneo)
             * - Si >30s: Muestra cache + refetch en background (UX fluido)
             *
             * Beneficio: UI instantánea + datos frescos
             */
            refetchOnMount: true,

            /**
             * ❌ NO polling por defecto
             *
             * Polling = Hacer requests automáticos cada X segundos.
             *
             * Por qué NO:
             * - Desperdicia recursos del servidor
             * - Consume datos del usuario innecesariamente
             * - Los datos CRUD no cambian cada segundo
             *
             * Cómo habilitar si lo necesitas:
             * ```typescript
             * useQuery({
             *   queryKey: [...],
             *   queryFn: ...,
             *   refetchInterval: 10000, // Override: cada 10s
             * })
             * ```
             *
             * Casos de uso válidos para polling:
             * - Dashboard en tiempo real
             * - Estado de procesos largos (importaciones)
             * - Notificaciones/mensajes
             */
            refetchInterval: false,

            // ============================================
            // Error Handling
            // ============================================

            /**
             * Reintentar 2 veces en caso de error
             *
             * Beneficio: Recuperación automática de errores transitorios
             * (timeouts, 500 temporales, etc.)
             */
            retry: 2,

            /**
             * Delay exponencial entre reintentos
             *
             * Intento 1: 1s
             * Intento 2: 2s
             * Máximo: 30s
             *
             * Beneficio: No sobrecarga el servidor con reintentos agresivos
             */
            retryDelay: (attemptIndex) =>
              Math.min(1000 * 2 ** attemptIndex, 30000),
          },

          mutations: {
            /**
             * Reintentar mutaciones 1 vez
             *
             * Más conservador que queries porque las mutaciones
             * pueden tener efectos secundarios.
             */
            retry: 1,
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}

      {/* DevTools solo en desarrollo */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools
          initialIsOpen={false}
          buttonPosition="bottom-right"
        />
      )}
    </QueryClientProvider>
  );
}
